import { useMemo, useState } from 'react'
import { toast } from '@dd3tech/wiggui/Toast'
import { generateMiliSecondsByMinutes, normalizeString } from '@wiggot/utils'
import {
  fetchListAgencyUsersV2,
  patchReassignAdviserV2
} from '../../../services'
import { useGet } from '../../../hooks'
import { useModalReassignAdviserV2Store } from '../../../store'
import type { ItemUser } from '../../../../interfaces/src'

export const useReassignAdviserV2 = () => {
  const [isLoadingReassignAdviser, setIsLoadingReassignAdviser] = useState<{
    isLoading: boolean
    userId: number | undefined
  }>({ isLoading: false, userId: undefined })
  const [show, setShow] = useState(false)
  const [adviserSelected, setAdviserSelected] = useState<ItemUser | undefined>(
    undefined
  )

  const [nameAdviser, setNameAdviser] = useState('')

  const modalReassignAdviserV2 = useModalReassignAdviserV2Store(
    (state) => state?.modalReassignAdviserV2
  )
  const reset = useModalReassignAdviserV2Store((state) => state?.reset)

  const onSuccess = modalReassignAdviserV2?.onSuccess

  const onClose = () => {
    setAdviserSelected(undefined)
    setNameAdviser('')
    setShow(false)
    reset()
  }

  const { data: resultSearch, isLoading } = useGet({
    queryKey: ['find-brokers'],
    queryFn: async () => {
      const res = await fetchListAgencyUsersV2()
      if (res.isSuccess && res.data) {
        return res.data
      }

      return []
    },
    options: {
      staleTime: generateMiliSecondsByMinutes(20),
      gcTime: generateMiliSecondsByMinutes(20)
    }
  })

  const fetchReassignedAdviser = async ({
    userId,
    ids
  }: {
    userId: number
    ids: string[]
  }) => {
    setIsLoadingReassignAdviser({ userId, isLoading: true })
    try {
      const { isSuccess } = await patchReassignAdviserV2({
        userId,
        propertyIds: ids
      })
      if (isSuccess) {
        toast({
          type: 'success',
          title: 'El asesor se asignó con éxito'
        })
        onClose()
        onSuccess?.()
      } else {
        toast({
          type: 'error',
          title: 'No se asignó al asesor'
        })
      }
    } catch (error) {
      toast({
        type: 'error',
        title: 'No se asignó al asesor'
      })
    } finally {
      setIsLoadingReassignAdviser({ userId: undefined, isLoading: false })
    }
  }

  const filteredOptions = useMemo(() => {
    if (!resultSearch) {
      return []
    }

    if (!nameAdviser) {
      return resultSearch?.slice(0, 30)
    }

    return resultSearch
      .filter((option) =>
        normalizeString(option?.fullName)
          ?.toLowerCase()
          ?.includes(nameAdviser.toLowerCase())
      )
      ?.slice(0, 30)
  }, [resultSearch, nameAdviser])

  const handleAssignedAdvisor = () => {
    if (!adviserSelected) {
      return
    }

    fetchReassignedAdviser({
      userId: adviserSelected.id,
      ids: modalReassignAdviserV2?.propertiesIds
    })
  }

  const handleChangeAdvisor = async (broker: ItemUser) => {
    if (broker?.fullName === adviserSelected?.fullName) {
      setAdviserSelected(undefined)
      return
    }

    setAdviserSelected(broker)
  }

  return {
    handleAssignedAdvisor,
    isLoading,
    adviserSelected,
    nameAdviser,
    setNameAdviser,
    filteredOptions,
    handleChangeAdvisor,
    modalReassignAdviserV2,
    onClose,
    show,
    setShow,
    isLoadingReassignAdviser,
    type: modalReassignAdviserV2?.type,
    mode: modalReassignAdviserV2?.mode
  }
}
